import {
  Attendance,
  AttendanceMonth,
  getCurrentAttendance,
  getOriginalAttendance,
  saveAttendance,
} from "@/api/openapi";
import { AttendancesContext, PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { attendanceToTempAttendance } from "../../attendanceUtils";

export type AttendanceMobileEmployeeScreenTab = "hours" | "voci";

export function useAttendanceMobileEmployeeScreen() {
  const { lang, selectedMandateId } = useContext(PspContext);
  const {
    attendanceMonth,
    setAttendanceMonth,
    setTempAttendanceMonth,
    selectedMonth,
    selectedYear,
    setIsSubmitted,
    setSubmittedAt,
  } = useContext(AttendancesContext);

  const {
    employeeMatricola: employeeMatricolaString,
    employeeFiliale: employeeFilialeString,
    month: monthString,
    year: yearString,
  } = useParams();
  const month = monthString ? Number.parseInt(monthString) : NaN;
  const year = yearString ? Number.parseInt(yearString) : NaN;
  const employeeMatricola = employeeMatricolaString
    ? Number.parseInt(employeeMatricolaString)
    : NaN;
  const employeeFiliale = employeeFilialeString
    ? Number.parseInt(employeeFilialeString)
    : NaN;

  const noteRef = useRef<HTMLSpanElement>(null);
  const [noteIsClamped, setNoteIsClamped] = useState(true);
  const [noteIsExpanded, setNoteIsExpanded] = useState(false);
  const [restoreEmployeeConfirmationOpen, setRestoreEmployeeConfirmationOpen] =
    useState(false);

  const attendance = attendanceMonth?.attendances.find(
    (a) =>
      a.employee.matricola === employeeMatricola &&
      a.employee.filiale === employeeFiliale,
  );

  useEffect(() => {
    const noteElement = noteRef.current;
    if (!noteElement) return;

    const temp = noteElement.cloneNode(true) as HTMLSpanElement;

    temp.style.overflow = "visible";
    temp.style.position = "fixed";
    temp.style.visibility = "hidden";
    temp.style.height = "auto";
    temp.style.maxHeight = "none";
    temp.classList.remove("line-clamp-2");

    noteElement.parentElement?.appendChild(temp);

    const fullHeight = temp.getBoundingClientRect().height;
    const displayHeight = noteElement.getBoundingClientRect().height;

    setNoteIsClamped(fullHeight > displayHeight);

    return function () {
      temp.remove();
    };
  }, [attendance?.note]);

  const [selectedTab, setSelectedTab] =
    useState<AttendanceMobileEmployeeScreenTab>("hours");
  const [editNoteBottomSheetOpen, setEditNoteBottomSheetOpen] = useState(false);
  const [note, setNote] = useState(
    attendanceMonth?.attendances.find(
      (a) =>
        a.employee.matricola === employeeMatricola &&
        a.employee.filiale === employeeFiliale,
    )?.note ?? "",
  );

  useEffect(() => {
    if (!attendanceMonth && selectedMandateId) {
      getCurrentAttendance({
        query: { mandate_id: selectedMandateId, month, year },
      }).then(({ data, error }) => {
        if (error) {
          toast(translate("somethingWentWrong", lang));
        } else {
          setAttendanceMonth(data.data);
          setTempAttendanceMonth(attendanceToTempAttendance(data.data));
          setIsSubmitted(data.isSubmitted);
          setSubmittedAt(data.submittedAt ? new Date(data.submittedAt) : null);
          setNote(
            data.data.attendances.find(
              (a) =>
                a.employee.matricola === employeeMatricola &&
                a.employee.filiale === employeeFiliale,
            )?.note ?? "",
          );
        }
      });
    } else if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
    }
  }, [
    attendanceMonth,
    employeeFiliale,
    employeeMatricola,
    lang,
    month,
    selectedMandateId,
    setAttendanceMonth,
    setIsSubmitted,
    setSubmittedAt,
    year,
  ]);

  function openNoteBottomSheet() {
    if (!attendance) {
      toast(translate("somethingWentWrong", lang));
      throw new Error("Tried to edit note but attendance is undefined.");
    }

    setNote(attendance.note);
    setEditNoteBottomSheetOpen(true);
  }

  function closeNoteBottomSheet() {
    if (!attendanceMonth) {
      toast(translate("somethingWentWrong", lang));
      return;
    }
    setEditNoteBottomSheetOpen(false);
    setTimeout(() => {
      setNote(
        attendanceMonth.attendances.find(
          (a) =>
            a.employee.matricola === employeeMatricola &&
            a.employee.filiale === employeeFiliale,
        )?.note ?? "",
      );
    }, 250);
  }

  function onSaveNote() {
    if (!attendanceMonth) {
      toast(translate("somethingWentWrong", lang));
      return;
    }
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }
    const newNoteAttendanceMonth: AttendanceMonth = {
      ...attendanceMonth,
      attendances: attendanceMonth.attendances.map(
        (a) =>
          (a.employee.matricola === employeeMatricola &&
          a.employee.filiale === employeeFiliale
            ? {
                ...a,
                note: note,
              }
            : a) satisfies Attendance,
      ),
    };

    saveAttendance({
      body: {
        data: newNoteAttendanceMonth.attendances,
        mandateId: selectedMandateId,
        month: selectedMonth,
        year: selectedYear,
      },
      query: {
        draft: true,
      },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        return;
      }

      setAttendanceMonth(data);
      closeNoteBottomSheet();
      toast(translate("noteSavedSuccessfully", lang));
    });
  }

  function onRestoreEmployee() {
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (!attendanceMonth) {
      toast(translate("somethingWentWrong", lang));
      throw new Error(
        "Tried restoring an employee, but attendanceMonth is undefined.",
      );
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }
    getOriginalAttendance({
      query: {
        mandate_id: selectedMandateId,
        month: selectedMonth,
        year: selectedYear,
      },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        throw error;
      }

      const originalEmployeeAttendance = data.data.attendances.find(
        (a) =>
          a.employee.matricola === employeeMatricola &&
          a.employee.filiale === employeeFiliale,
      );
      if (!originalEmployeeAttendance) {
        toast(translate("somethingWentWrong", lang));
        throw new Error(
          "Tried to restore an employee that was not found in the original attendance version.",
        );
      }

      const newAttendance = attendanceMonth?.attendances.map((a) =>
        a.employee.matricola === employeeMatricola &&
        a.employee.filiale === employeeFiliale
          ? originalEmployeeAttendance
          : a,
      );

      saveAttendance({
        body: {
          data: newAttendance,
          mandateId: selectedMandateId,
          month: selectedMonth,
          year: selectedYear,
        },
        query: { draft: true },
      }).then(({ error, data }) => {
        if (error) {
          toast(translate("somethingWentWrong", lang));
          throw error;
        }

        setAttendanceMonth(data);
        setTempAttendanceMonth(attendanceToTempAttendance(data));
        setRestoreEmployeeConfirmationOpen(false);
        toast(translate("employeeRestoredSuccessfully", lang));
      });
    });
  }

  return {
    lang,
    attendance,
    month,
    year,
    editNoteBottomSheetOpen,
    note,
    setNote,
    closeNoteBottomSheet,
    onSaveNote,
    noteIsClamped,
    noteRef,
    noteIsExpanded,
    setNoteIsExpanded,
    openNoteBottomSheet,
    selectedTab,
    setSelectedTab,
    employeeMatricola,
    employeeFiliale,
    setRestoreEmployeeConfirmationOpen,
    restoreEmployeeConfirmationOpen,
    onRestoreEmployee,
  };
}
